<template>
  <div
    :class="`checkbox-container ${noteRequiredField ? 'required-field' : ''} ${computedColumns ? 'columns-' + computedColumns : ''}`"
    style="width: 100%; position: relative"
  >
    <div v-if="previousValue">
      <input-history
        :title="label"
        :value="previousValue"
        :dos="previousValueDos"
      />
      Previous Selections
    </div>

    <label :class="`${label ? '' : 'empty-label'} ${labelTitle ? 'label-title' : ''}`">
      <strong v-if="noteRequiredField && label" class="error--text">*</strong>
      {{ label }}
    </label>

    <v-row
      class="checkbox-group"
      :class="columnSort ? 'flex-column' : ''"
      :style="columnSort ? `max-height: ${computedFieldHeight}px` : ''"
    >
      <v-col
        v-for="(item, i) in items"
        :key="`item-${i}`"
        :cols="12 / computedColumns"
        class="pa-0"
      >
        <div class="checkbox-wrapper">
          <v-checkbox
            v-model="selectedValues"
            :value="returnObject ? item : item[computedValue]"
            :ripple="false"
            :disabled="disabled"
            :hide-details="hideDetails"
            :rules="computedRules"
            :off-icon="radioButtons ? '$radioOff' : '$checkboxOff'"
            :on-icon="radioButtons ? '$radioOn' : '$checkboxOn'"
            :append-icon="i === items.length - 1 ? computedAppendIcon : null"
            :class="[
                'no-label-checkbox',
                'disable-min-width',
                { rounded: radioButtons },
                (noteRequiredField && label === '' && !computedColumns) ? 'required-checkbox' : '',
              ]"
            @change="emitChanges($event)"
            @click="itemClicked($event)"
            @click:append="clickAppendIcon($event)"
          ></v-checkbox>

          <div class="non-clickable-label">
            <span class="label-text">{{ item[computedLabel] }}</span>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-lonely-if */
export default {
  props: {
    value: {
      type: Array,
      default: null,
    },
    items: {
      type: Array,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    columnSort: {
      type: Boolean,
      default: false,
    },
    column: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: String,
      default: null,
    },
    itemValue: {
      type: String,
      default: null,
    },
    itemLabel: {
      type: String,
      default: null,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    labelTitle: {
      type: Boolean,
      default: false,
    },
    distinct: {
      type: [Array, String],
      default: null,
    },
    radioButtons: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    appendIcon: {
      type: String,
      default: null,
    },
    previousValue: {
      type: [String, Boolean, Number],
      default: null,
    },
    previousValueDos: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      selectedValues: [...this.sanitizedValues()],
      index: 0,
    }
  },
  computed: {
    noteRequiredField() {
      return !this.disabled && this.required && this.selectedValues.length === 0
    },
    computedRules() {
      return (this.required && !this.disabled) ? this.$validationRules.requiredCheckboxes : []
    },
    computedColumns() {
      if (this.column || this.columns !== null) {
        return this.columns ? this.columns : 1
      }

      return false
    },
    computedValue() {
      if (this.itemValue) return this.itemValue
      if (this.items && this.items.length) {
        if (typeof this.items[0].id !== 'undefined') return 'id'
        if (typeof this.items[0].value !== 'undefined') return 'value'
        if (typeof this.items[0].title !== 'undefined') return 'title'
        if (typeof this.items[0].label !== 'undefined') return 'label'
      }

      return 'value'
    },
    computedLabel() {
      if (this.itemLabel) return this.itemLabel
      if (this.items && this.items.length) {
        if (typeof this.items[0].title !== 'undefined') return 'title'
      }

      return 'label'
    },
    computedFieldHeight() {
      return Math.ceil(this.items.length / this.computedColumns) * (this.hideDetails ? 51 : 53)
    },
    computedAppendIcon() {
      if (this.appendIcon) return this.appendIcon
      if (this.clearable) {
        return this.selectedValues.length ? '$close' : null
      }

      return null
    },
  },
  watch: {
    value() {
      this.selectedValues = [...this.sanitizedValues()]
    },
  },
  methods: {
    emitChanges(values) {
      this.$emit('change', values)
      this.$emit('input', values)
    },
    getCheckbox(event) {
      // With a checkbox, you can click on either the box or the label, so this nonsense is required if the user clicks the label, or outside the box or label
      switch (event.target?.tagName) {
        case 'INPUT': return event.target
        case 'LABEL': return event.target?.previousElementSibling?.querySelector('input')
        default: return event.target?.querySelector('input')
      }
    },
    itemClicked(event) {
      if (this.radioButtons) {
        // nextTick to make sure the click has finished changing the selected values
        this.$nextTick(() => {
          this.emitChanges([this.getCheckbox(event)?._value])
        })
      } else if (this.distinct) {
        // nextTick to make sure the click has finished changing the selected values
        this.$nextTick(() => {
          const checkbox = this.getCheckbox(event)
          const title = checkbox?._value?.title

          const newSelectedValues = this.distinct.includes(title)
            ? this.selectedValues.filter(x => this.distinct.includes(x.title))
            : this.selectedValues.filter(x => !this.distinct.includes(x.title))

          // Only emit and notify the user if there were actually changes
          if (this.$custom.notEmpty(this.$lodash.xor(this.selectedValues, newSelectedValues))) {
            this.emitChanges(newSelectedValues)
            const distinctList = this.$custom.toList(this.distinct, null, '&', 'none').trim()
            const isAre = (this.$custom.isArray(this.distinct) && this.distinct.length > 1) ? 'are' : 'is'
            this.$store.dispatch('notify', { value: `Option(s) unselected as '${distinctList}' ${isAre} exclusive.`, color: 'warning' })
          }
        })
      }
      this.$emit('click', event)
    },
    sanitizedValues() {
      if (this.value.length && this.value[0]?._joinData) {
        return this.value.map(item => ({ id: item.id, title: item.title }))
      }

      return this.value
    },
    clickAppendIcon(event) {
      if (this.clearable) this.emitChanges([])
      this.$emit('click:append', event)
    },
  },
}
</script>

<style lang="scss">
.checkbox-container {
  position: relative;

  label {
    display: block;
    font-weight: 500;
    margin-bottom: 4px;

    &.empty-label {
      font-style: italic;
      color: gray;
    }

    &.label-title {
      font-size: 1.1em;
    }

    strong.error--text {
      color: var(--v-error-base);
      margin-right: 2px;
    }
  }

  &.required-field .required-checkbox:first-child::before {
    content: '*';
    color: var(--v-error-base);
    font-weight: 700;
    position: relative;
    top: 14px;
    height: 15px;
  }

  .checkbox-group {
    display: flex;
    flex-wrap: wrap;

    &.flex-column {
      flex-direction: column;
    }
  }

  .checkbox-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;

    > .v-input.v-input--checkbox:not(.rounded) ~ .non-clickable-label {
      padding-left: 0px !important;
    }

    > .v-input.v-input--checkbox.rounded ~ .non-clickable-label {
      padding-left: 4px !important;
    }
    .v-input--checkbox {
      flex: 0 0 auto;
    }

    .non-clickable-label {
      flex: 0 1 auto;
      display: flex;
      align-items: center;
      cursor: default;
      user-select: none;
      word-break: break-word;
      padding: 0;
      .label-text {
        width: 100%;
        padding: 0 0 4px 4px;
      }
    }
  }
}
.checkbox-wrapper .v-input--checkbox .v-input__slot {
  padding-left: 0px !important;
}
.columns-3 .v-input--radio-group__input .v-radio, .columns-3 .v-input--checkbox {
    min-width: 0 !important;
}
input-history {
  display: block;
  margin-bottom: 4px;
}
.checkbox-container .v-input--radio {
  margin-bottom: 8px;
}
.checkbox-group .v-input--checkbox,
.checkbox-group .v-input--radio {
  min-width: 0 !important;
  flex: 0 0 auto;
}
.checkbox-group > div {
  margin-right: 0px !important;
}
.v-input.v-input--radio-group .v-radio {
  min-width: 0 !important;
}
.checkbox-wrapper .v-input--checkbox .v-input__append-outer {
  position: absolute;
  right: 0;
  top: 25%;
  transform: translateY(-50%);
  z-index: 1;
}
</style>
